import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faCalendarAlt, faMapMarkerAlt, faUsers, faClock } from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function AddProgram() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    speaker_name: '',
    type: 'workshop',
    program_date: '',
    program_time: '10:00',
    end_date: '',
    end_time: '17:00',
    location: '',
    location_details: '',
    capacity: '',
    member_price: '',
    non_member_price: '',
    status: 'upcoming',
    featured: false
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required field validations
    if (!formData.title) newErrors.title = 'Program title is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.program_date) newErrors.program_date = 'Start date is required';
    if (!formData.program_time) newErrors.program_time = 'Start time is required';
    if (!formData.location) newErrors.location = 'Location is required';
    
    // Length validations
    if (formData.title && formData.title.length > 100) {
      newErrors.title = 'Title should be 100 characters or less';
    }
    
    if (formData.description && formData.description.length > 5000) {
      newErrors.description = 'Description should be 5000 characters or less';
    }
    
    if (formData.speaker_name && formData.speaker_name.length > 100) {
      newErrors.speaker_name = 'Speaker name should be 100 characters or less';
    }
    
    if (formData.location && formData.location.length > 200) {
      newErrors.location = 'Location should be 200 characters or less';
    }
    
    // Date validations
    if (formData.end_date && formData.program_date) {
      const startDate = new Date(formData.program_date);
      const endDate = new Date(formData.end_date);
      
      if (endDate < startDate) {
        newErrors.end_date = 'End date cannot be before start date';
      } else if (endDate.getTime() === startDate.getTime()) {
        // If same day, validate end time is after start time
        if (formData.end_time && formData.program_time) {
          const startTime = formData.program_time;
          const endTime = formData.end_time;
          
          if (endTime <= startTime) {
            newErrors.end_time = 'End time must be later than start time';
          }
        }
      }
    }
    
    // Numeric validations
    if (formData.capacity) {
      if (isNaN(formData.capacity)) {
        newErrors.capacity = 'Capacity must be a number';
      } else if (parseInt(formData.capacity) < 0) {
        newErrors.capacity = 'Capacity cannot be negative';
      } else if (parseInt(formData.capacity) > 10000) {
        newErrors.capacity = 'Capacity seems too high (max: 10,000)';
      }
    }
    
    // Member price validations
    if (formData.member_price) {
      if (isNaN(formData.member_price)) {
        newErrors.member_price = 'Member price must be a number';
      } else {
        const fee = parseFloat(formData.member_price);
        if (fee < 0) {
          newErrors.member_price = 'Member price cannot be negative';
        } else if (fee > 100000) {
          newErrors.member_price = 'Member price seems too high (max: ₹100,000)';
        } else if (!(/^\d+(\.\d{1,2})?$/).test(formData.member_price)) {
          newErrors.member_price = 'Member price should have at most 2 decimal places';
        }
      }
    }
    
    // Non-member price validations
    if (formData.non_member_price) {
      if (isNaN(formData.non_member_price)) {
        newErrors.non_member_price = 'Non-member price must be a number';
      } else {
        const fee = parseFloat(formData.non_member_price);
        if (fee < 0) {
          newErrors.non_member_price = 'Non-member price cannot be negative';
        } else if (fee > 100000) {
          newErrors.non_member_price = 'Non-member price seems too high (max: ₹100,000)';
        } else if (!(/^\d+(\.\d{1,2})?$/).test(formData.non_member_price)) {
          newErrors.non_member_price = 'Non-member price should have at most 2 decimal places';
        }
      }
    }
    
    // Date range validation
    if (formData.program_date) {
      const startDate = new Date(formData.program_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // For past events
      if (formData.status !== 'completed' && formData.status !== 'cancelled' && startDate < today) {
        newErrors.program_date = 'Start date cannot be in the past for upcoming or ongoing events';
      }
      
      // Prevent unreasonably far future dates (e.g., more than 2 years in advance)
      const twoYearsFromNow = new Date();
      twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
      if (startDate > twoYearsFromNow) {
        newErrors.program_date = 'Start date cannot be more than 2 years in the future';
      }
    }
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to the first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format date and time for API submission
      const formatDateTimeForAPI = (dateString, timeString) => {
        if (!dateString) return null;
        // Combine date and time strings into ISO format
        return `${dateString}T${timeString}:00`;
      };
      
      // Format data for API submission
      const programData = {
        ...formData,
        program_date: formatDateTimeForAPI(formData.program_date, formData.program_time),
        end_date: formData.end_date ? formatDateTimeForAPI(formData.end_date, formData.end_time) : null,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        member_price: formData.member_price ? parseFloat(formData.member_price) : 0,
        non_member_price: formData.non_member_price ? parseFloat(formData.non_member_price) : 0
      };
      
      // Submit to API
      await adminService.createProgram(programData);
      
      // Redirect to programs list page after successful submission
      navigate('/admin/programs');
    } catch (error) {
      console.error('Error creating program:', error);
      setErrors({
        api: 'Failed to create program. Please try again.'
      });
      
      // Scroll to top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Add Program</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/programs">Programs</Link>
          </li>
          <li className="admin-breadcrumb-item active">Add Program</li>
        </ul>
      </div>
      
      {errors.api && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '0.75rem 1.25rem', 
          marginBottom: '1rem', 
          borderRadius: '0.25rem' 
        }}>
          {errors.api}
        </div>
      )}
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Program Information</h2>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-group">
              <label htmlFor="title" className="admin-form-label">Program Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`admin-form-control ${errors.title ? 'admin-input-error' : ''}`}
                placeholder="e.g., Weekend Workshop Series on Valuation"
              />
              {errors.title && <div className="admin-error-message">{errors.title}</div>}
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="type" className="admin-form-label">Program Type *</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="admin-form-control"
                >
                  <option value="workshop">Workshop</option>
                  <option value="webinar">Webinar</option>
                  <option value="course">Course</option>
                  <option value="conference">Conference</option>
                  <option value="seminar">Seminar</option>
                </select>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="status" className="admin-form-label">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="admin-form-control"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="description" className="admin-form-label">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={`admin-form-control ${errors.description ? 'admin-input-error' : ''}`}
                rows="4"
                placeholder="Provide a detailed description of the program"
              ></textarea>
              {errors.description && <div className="admin-error-message">{errors.description}</div>}
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="speaker_name" className="admin-form-label">Speaker Name</label>
              <input
                type="text"
                id="speaker_name"
                name="speaker_name"
                value={formData.speaker_name}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="e.g., Dr. John Doe, CFA"
              />
              <small className="admin-form-text">Name of the primary speaker or presenter</small>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="program_date" className="admin-form-label">Start Date *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="program_date"
                    name="program_date"
                    value={formData.program_date}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.program_date ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.program_date && <div className="admin-error-message">{errors.program_date}</div>}
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="program_time" className="admin-form-label">Start Time *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faClock} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="time"
                    id="program_time"
                    name="program_time"
                    value={formData.program_time}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.program_time ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.program_time && <div className="admin-error-message">{errors.program_time}</div>}
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="end_date" className="admin-form-label">End Date</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    className="admin-form-control"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                <small className="admin-form-text">Leave empty for single-day events</small>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="end_time" className="admin-form-label">End Time</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faClock} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="time"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    className="admin-form-control"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 2 }}>
                <label htmlFor="location" className="admin-form-label">Location *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.location ? 'admin-input-error' : ''}`}
                    placeholder="e.g., BVA Conference Hall, Bangalore"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.location && <div className="admin-error-message">{errors.location}</div>}
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="capacity" className="admin-form-label">Capacity</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faUsers} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="number"
                    id="capacity"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.capacity ? 'admin-input-error' : ''}`}
                    placeholder="e.g., 50"
                    min="0"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.capacity && <div className="admin-error-message">{errors.capacity}</div>}
                <small className="admin-form-text">Leave empty for unlimited capacity</small>
              </div>
            </div>
            
            <div className="admin-form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="location_details" className="admin-form-label">Location Details</label>
              <textarea
                id="location_details"
                name="location_details"
                value={formData.location_details}
                onChange={handleChange}
                className="admin-form-control"
                rows="2"
                placeholder="Additional location details, directions, etc."
              ></textarea>
            </div>

            <div className="admin-form-row" style={{ marginBottom: '20px' }}>
              <div className="admin-form-group" style={{ flex: 1, marginRight: '15px' }}>
                <label htmlFor="member_price" className="admin-form-label">Member Registration Fee (₹)</label>
                <input
                  type="text"
                  id="member_price"
                  name="member_price"
                  value={formData.member_price}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.member_price ? 'admin-input-error' : ''}`}
                  placeholder="e.g., 400"
                />
                {errors.member_price && <div className="admin-error-message">{errors.member_price}</div>}
                <small className="admin-form-text">Leave empty for free registration for members</small>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="non_member_price" className="admin-form-label">Non-Member Registration Fee (₹)</label>
                <input
                  type="text"
                  id="non_member_price"
                  name="non_member_price"
                  value={formData.non_member_price}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.non_member_price ? 'admin-input-error' : ''}`}
                  placeholder="e.g., 600"
                />
                {errors.non_member_price && <div className="admin-error-message">{errors.non_member_price}</div>}
                <small className="admin-form-text">Leave empty for free registration for non-members</small>
              </div>
            </div>
            
            <div className="admin-form-group" style={{ marginTop: '10px', marginBottom: '30px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem' }}
                />
                <label htmlFor="featured">
                  <strong>Feature this program</strong><br />
                  <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>Featured programs appear prominently on the website</span>
                </label>
              </div>
            </div>
            
            <div className="admin-form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Link
                to="/admin/programs"
                className="admin-btn admin-btn-light admin-btn-with-icon"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Cancel</span>
              </Link>
              <button
                type="submit"
                className="admin-btn admin-btn-primary admin-btn-with-icon"
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{isSubmitting ? 'Saving...' : 'Create Program'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AddProgram;
